import { generatePDF } from '../api';

async function urlZuBase64(url) {
  try {
    const absUrl = url.startsWith('http') ? url : new URL(url, window.location.href).href;
    const res = await fetch(absUrl);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function cssWithInlinedFonts(css) {
  const regex = /url\(['"]?([^'")\s]+\.(?:ttf|woff2?|otf))['"]?\)/gi;
  const matches = [...css.matchAll(regex)];
  for (const match of matches) {
    const base64 = await urlZuBase64(match[1]);
    if (base64) {
      css = css.replace(match[0], `url('${base64}')`);
    }
  }
  return css;
}

async function inlineImages(rootEl) {
  const images = Array.from(rootEl.querySelectorAll('img[src]'));
  await Promise.all(images.map(async (img) => {
    const src = img.getAttribute('src');
    if (!src || src.startsWith('data:') || src.startsWith('blob:')) return;
    const base64 = await urlZuBase64(src);
    if (base64) img.setAttribute('src', base64);
  }));
}

/**
 * Serializes a rendered container element into a self-contained HTML string,
 * sends it to the Playwright backend, and triggers a direct browser download.
 *
 * @param {HTMLElement} containerEl - e.g. cvRef.current (.cv-paginas-container)
 * @param {string} filename - output filename, e.g. 'CV_Max_Mustermann.pdf'
 * @param {{nurBlob?: boolean}} [opts] - nurBlob: gibt das PDF-Blob zurueck statt es herunterzuladen
 *        (fuer Drag-and-Drop auf den Desktop, das die Datei vorab braucht).
 */
export async function generateAndDownloadPDF(containerEl, filename, { nurBlob = false } = {}) {
  // 1. Collect all CSS currently loaded in the page
  let css = Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules).map((r) => r.cssText).join('\n');
      } catch {
        return '';
      }
    })
    .join('\n');

  // Embed fonts as base64 so Playwright doesn't need network access for fonts
  css = await cssWithInlinedFonts(css);

  // 2. Clone container and remove interactive UI overlays.
  //    .an-geparkt gehört nicht dazu: das sind Briefblöcke, die über die Blattkante
  //    hinausragen. Sie stehen bewusst nicht im fertigen Dokument — schon ein
  //    Millimeter Überstand genügt, so hat der Nutzer es festgelegt.
  const clone = containerEl.cloneNode(true);
  clone.querySelectorAll(
    '.cv-drag-row, .cv-drag-handle, .cv-drag-handle-btn, .ki-btn-sektion, .an-edit-overlay, .an-ki-btn, .an-edit-btn, .cv-history-btns, .cv-pf-foto-placeholder, .cv-bearbeiten-stift, .an-resize-handle, .an-snap-line, .an-fixed-toolbar, .an-geparkt, ' +
    // Bedienelemente des Bearbeitungsmodus. Sie liegen INNERHALB des Kastens, der hier
    // vervielfältigt wird — wer das PDF erzeugt, ohne den Bearbeitungsmodus vorher zu
    // verlassen, hätte sonst „Seite hinzufügen", die Seitenzahlen, den Löschknopf und
    // die Spuren ausgeblendeter Blöcke im fertigen Brief stehen.
    '.an-seite-neu, .an-seite-nummer, .an-seite-weg, .an-versteckt-spur'
  ).forEach((el) => el.remove());

  // Der Bearbeitungsmodus hängt am Blatt selbst und lässt sich nicht wegnehmen, indem
  // man ein Element entfernt — er ist eine Klasse. An ihr hängen der gestrichelte
  // Sicherheitsrand und der rote Warnstreifen. Wird das PDF erzeugt, während der
  // Bearbeitungsmodus noch läuft, landeten beide im fertigen Brief. Die Klasse fällt
  // deshalb ab; damit rendert das Blatt genau so wie in der reinen Ansicht.
  clone.querySelectorAll('.an-pagina--bearbeiten').forEach((el) => {
    // `--aktiv` markiert die Seite, auf der gearbeitet wird. Ihr Ring würde vom
    // Zurücksetzen des Schlagschattens weiter unten zwar mit verschwinden, aber nur
    // als Nebenwirkung — verlässt sich besser niemand darauf.
    el.classList.remove('an-pagina--bearbeiten', 'an-pagina--randverletzt', 'an-pagina--aktiv');
  });

  // Dieselbe Sorte Problem eine Ebene tiefer: Der grüne Rahmen um den ausgewählten Block
  // ist ebenfalls nur eine Klasse. Im Stylesheet steht dagegen zwar ein Netz für den
  // Druck, aber es hing lange nur zum Schein — die Regel für den ausgewählten Block kam
  // in derselben Datei später und war gleich stark, gewann also. Der Rahmen landete im
  // fertigen PDF; gemeldet vom User am 2026-08-11.
  //
  // Das Netz ist repariert, doch hier fällt die Klasse zusätzlich weg. Das ist bewusst
  // doppelt: Diese Zeile wirkt unabhängig davon, ob der Druck-Modus überhaupt greift,
  // und niemand muss beim nächsten Umsortieren des Stylesheets daran denken.
  clone.querySelectorAll('.an-selected').forEach((el) => el.classList.remove('an-selected'));

  // Embed photos/images so backend Playwright doesn't depend on external image loading
  await inlineImages(clone);

  // Remove keyword highlights — cv-keyword-mark spans become plain text
  clone.querySelectorAll('.cv-keyword-mark').forEach((span) => {
    span.replaceWith(span.textContent);
  });

  // Remove box-shadow and margin from page elements for clean PDF output
  clone.querySelectorAll('.cv-pagina, .an-pagina').forEach((el) => {
    el.style.boxShadow = 'none';
    el.style.margin = '0';
    el.style.borderRadius = '0'; // rounded corners look nice on screen but clip content in PDF
    el.style.zoom = '1'; // reset screen scale — PDF always exports at full A4 size
  });

  // Das Anschreiben wird auf dem Bildschirm verkleinert gezeigt, und zwar am Kasten um
  // die Blätter — nicht am Blatt selbst. Der Reset eine Zeile höher greift dort also
  // nicht, und ohne diesen hier käme ein um ein Zehntel geschrumpftes PDF heraus.
  clone.querySelectorAll('.an-paginas-container').forEach((el) => { el.style.zoom = '1'; });

  // CV last page: fix to full A4 so absolute-positioned footer stays at bottom
  // AN pages are NOT height-capped — Playwright paginates naturally if content overflows
  const cvSeiten = clone.querySelectorAll('.cv-pagina');
  if (cvSeiten.length > 0) {
    const letzte = cvSeiten[cvSeiten.length - 1];
    letzte.style.height = '297mm';
    letzte.style.minHeight = '297mm';
  }

  // Ensure container background is white, not dark
  clone.style.background = 'white';

  const html = `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<style>
body { margin: 0; padding: 0; background: white; }
${css}
/* PDF-Overrides: dunkle Hintergründe des Studio-Containers entfernen */
html, body { background: white !important; }
.cv-paginas-container, .an-paginas-container { background: white !important; }
/* Container padding/gap must be 0 in PDF: any spacing offsets the exact-A4 pages
   past the physical page boundary, spilling a sliver onto a blank overflow page. */
.cv-paginas-container, .an-paginas-container { padding: 0 !important; gap: 0 !important; }
/* Gedruckt wird immer in Originalgröße — die Verkleinerung ist eine reine Sehhilfe. */
.an-paginas-container { zoom: 1 !important; }
/* Each page prints on its own physical sheet. Height a hair under A4 so Chromium's
   mm→px rounding never spills a sub-pixel sliver onto a blank trailing sheet.
   break-after: page forces a clean sheet per page; last page must NOT break
   (a forced break after the last page emits an extra blank sheet). */
.cv-pagina, .an-pagina {
  overflow: hidden !important; margin: 0 !important;
  height: 296mm !important; min-height: 296mm !important;
  break-after: page !important; page-break-after: always !important;
}
.cv-pagina:last-child, .an-pagina:last-child {
  break-after: auto !important; page-break-after: auto !important;
}
.an-edit-btn, .cv-drag-handle, .cv-drag-handle-btn, .ki-btn-sektion { display: none !important; }
.cv-keyword-mark, .cv-keyword-mark::before, .cv-keyword-mark::after {
  background: none !important; color: inherit !important; box-shadow: none !important;
}
[data-tooltip]::after, [data-tooltip]::before { display: none !important; }
</style>
</head>
<body>
${clone.outerHTML}
</body>
</html>`;

  // 3. POST to backend, receive PDF blob
  const response = await generatePDF(html, filename);

  // Nur das Blob zurueckgeben (Aufrufer kuemmert sich um URL/Cleanup).
  if (nurBlob) return response.data;

  // 4. Trigger browser download — no dialog
  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return response.data;
}
